import { z } from 'zod';

import { BorrowerId, DocumentId, PaymentId, PayoffId, PrincipalModificationId } from '../BrandedIds';
import { EmailAttachment } from '../EmailAttachment';
import {
  BaseEmailTemplateType,
  BaseEmailWithAttachmentTemplateType,
  BorrowerInsuranceExpiredEmail,
  BorrowerInsuranceExpiringSoonEmail,
  DocumentEmailTemplateType,
  GoodbyeBeforeWelcome,
  GoodbyeEmail,
  LenderPaymentFailedEmail,
  PaidInFullStatementEmail,
  PartialPayoffConfirmationEmail,
  PaymentEmailTemplateType,
  PaymentFailedAutopayEmail,
  PrincipalModificationAppliedEmail,
  PrincipalModificationCancelledEmail,
  UpdateAutopayEmailTemplateType,
} from '../emails/Templates';
import { BorrowerInsuranceExpirationInformation } from '../loan/BorrowerInsuranceExpirationInformation';
import { BaseLoanEvent } from './BaseLoanEvent';

export const SendEmailOptions = z
  .object({
    disableEmail: z.boolean().optional(),
    disableDocument: z.boolean().optional(),
    disableAuditLog: z.boolean().optional(),
  })
  .optional();

const SendBaseEmailEvent = z.object({
  ...BaseLoanEvent.shape,
  type: z.literal('sendEmail'),
  borrowerId: BorrowerId,
  template: BaseEmailTemplateType,
  opts: SendEmailOptions,
});
type SendBaseEmailEvent = z.infer<typeof SendBaseEmailEvent>;

const SendGoodbyeEmailEvent = z.object({
  ...SendBaseEmailEvent.shape,
  template: GoodbyeEmail,
  isUpdate: z.boolean().optional(),
});
type SendGoodbyeEmailEvent = z.infer<typeof SendGoodbyeEmailEvent>;

const SendGoodbyeBeforeWelcomeEmailEvent = z.object({
  ...SendBaseEmailEvent.shape,
  template: GoodbyeBeforeWelcome,
  isUpdate: z.boolean().optional(),
});
const SendPaidInFullStatementEmailEvent = z.object({
  ...SendBaseEmailEvent.shape,
  template: PaidInFullStatementEmail,
  isUpdate: z.boolean().optional(),
});
type SendPaidInFullStatementEmailEvent = z.infer<typeof SendPaidInFullStatementEmailEvent>;

const SendEmailWithAttachmentEvent = z.object({
  ...SendBaseEmailEvent.shape,
  template: BaseEmailWithAttachmentTemplateType,
  attachments: z.array(EmailAttachment),
});
type SendEmailWithAttachmentEvent = z.infer<typeof SendEmailWithAttachmentEvent>;

const SendPaymentEmailEvent = z.object({
  ...SendBaseEmailEvent.shape,
  template: PaymentEmailTemplateType,
  paymentId: PaymentId,
});
type SendPaymentEmailEvent = z.infer<typeof SendPaymentEmailEvent>;

const SendPaymentFailedEmailEvent = z.object({
  ...SendBaseEmailEvent.shape,
  template: PaymentFailedAutopayEmail,
  paymentId: PaymentId.optional(),
  failureReason: z.string().optional(),
});
type SendPaymentFailedEmailEvent = z.infer<typeof SendPaymentFailedEmailEvent>;

const SendDwollaUpdateAutopayEmailEvent = z.object({
  ...SendBaseEmailEvent.shape,
  paymentProvider: z.literal('dwolla'),
  template: UpdateAutopayEmailTemplateType,
  autopayBankUrl: z.string(),
});
const SendUsioUpdateAutopayEmailEvent = z.object({
  ...SendBaseEmailEvent.shape,
  paymentProvider: z.literal('usio'),
  template: UpdateAutopayEmailTemplateType,
  confirmationId: z.string(),
});
const SendUpdateAutopayEmailEvent = z.discriminatedUnion('paymentProvider', [
  SendDwollaUpdateAutopayEmailEvent,
  SendUsioUpdateAutopayEmailEvent,
]);
type SendUpdateAutopayEmailEvent = z.infer<typeof SendUpdateAutopayEmailEvent>;

const SendDocumentEmailEvent = z.object({
  ...SendBaseEmailEvent.shape,
  template: DocumentEmailTemplateType,
  documentId: DocumentId,
});
type SendDocumentEmailEvent = z.infer<typeof SendDocumentEmailEvent>;

const SendLenderPaymentFailedEvent = z.object({
  ...SendBaseEmailEvent.shape,
  template: LenderPaymentFailedEmail,
  paymentId: PaymentId.optional(),
  failureReason: z.string().optional(),
});
type SendLenderPaymentFailedEvent = z.infer<typeof SendLenderPaymentFailedEvent>;

export const SendPartialPayoffConfirmationEmailEvent = z.object({
  ...SendBaseEmailEvent.shape,
  template: PartialPayoffConfirmationEmail,
  payoffId: PayoffId,
});
type SendPartialPayoffConfirmationEmailEvent = z.infer<typeof SendPartialPayoffConfirmationEmailEvent>;

export const SendPrincipalModificationAppliedEmailEvent = z.object({
  ...SendBaseEmailEvent.shape,
  template: PrincipalModificationAppliedEmail,
  principalModificationId: PrincipalModificationId,
});
type SendPrincipalModificationAppliedEmailEvent = z.infer<typeof SendPrincipalModificationAppliedEmailEvent>;

export const SendPrincipalModificationCancelledEmailEvent = z.object({
  ...SendBaseEmailEvent.shape,
  template: PrincipalModificationCancelledEmail,
  principalModificationId: PrincipalModificationId,
});
type SendPrincipalModificationCancelledEmailEvent = z.infer<typeof SendPrincipalModificationCancelledEmailEvent>;

export const SendBorrowerInsuranceExpiringSoonEmailEvent = z.object({
  ...SendBaseEmailEvent.shape,
  template: BorrowerInsuranceExpiringSoonEmail,
  insuranceExpirationInformation: BorrowerInsuranceExpirationInformation,
});
export type SendBorrowerInsuranceExpiringSoonEmailEvent = z.infer<typeof SendBorrowerInsuranceExpiringSoonEmailEvent>;

export const SendBorrowerInsuranceExpiredEmailEvent = z.object({
  ...SendBaseEmailEvent.shape,
  template: BorrowerInsuranceExpiredEmail,
  insuranceExpirationInformation: BorrowerInsuranceExpirationInformation,
});
export type SendBorrowerInsuranceExpiredEmailEvent = z.infer<typeof SendBorrowerInsuranceExpiredEmailEvent>;

export const SendEmailEvent = z.discriminatedUnion('template', [
  SendBaseEmailEvent,
  SendGoodbyeEmailEvent,
  SendGoodbyeBeforeWelcomeEmailEvent,
  SendPaidInFullStatementEmailEvent,
  SendEmailWithAttachmentEvent,
  SendPaymentEmailEvent,
  SendPaymentFailedEmailEvent,
  SendUpdateAutopayEmailEvent,
  SendDocumentEmailEvent,
  SendLenderPaymentFailedEvent,
  SendPartialPayoffConfirmationEmailEvent,
  SendPrincipalModificationAppliedEmailEvent,
  SendPrincipalModificationCancelledEmailEvent,
  SendBorrowerInsuranceExpiringSoonEmailEvent,
  SendBorrowerInsuranceExpiredEmailEvent,
]);
export type SendEmailEvent = z.infer<typeof SendEmailEvent>;
